import { useState, useEffect, useRef } from 'react';
import Link from '@mui/material/Link';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AdminDoc,
  AdminDocDivision,
  AdminDocFile,
  AdminDocStatus,
  ADMIN_DOC_DEPARTMENTS,
} from '../../../models/admin-doc.model';
import { onError } from '../../../utils/error.util';
import { useAppContext } from '../../../utils/content.util';
import {
  createAdminDoc,
  getAdminDoc,
  getAdminDocUploadUrl,
  updateAdminDoc,
} from '../../../services/admin-docs.service';

import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

const AdminDocSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string(),
  division: Yup.string()
    .oneOf([AdminDocDivision.OnboardingCCL, AdminDocDivision.OnboardingTSF, AdminDocDivision.CCL, AdminDocDivision.TSF, AdminDocDivision.Company])
    .required('Division is required'),
  department: Yup.string()
    .oneOf([...ADMIN_DOC_DEPARTMENTS])
    .required('Department is required'),
  status: Yup.string()
    .oneOf([AdminDocStatus.Open, AdminDocStatus.Done])
    .required('Status is required'),
});

const initialValues = {
  id: '',
  name: '',
  division: AdminDocDivision.CCL,
  department: ADMIN_DOC_DEPARTMENTS[0] as AdminDoc['department'],
  status: AdminDocStatus.Open,
  description: '',
  documents: [] as AdminDocFile[],
  createdAt: 0,
  updatedAt: 0,
};

function divisionToBrand(division: AdminDocDivision): 'tsf' | 'ccl' {
  return division === AdminDocDivision.OnboardingTSF || division === AdminDocDivision.TSF ? 'tsf' : 'ccl';
}

function getUploaderDisplayName(currentUser: { given_name?: string; family_name?: string } | undefined): string {
  if (!currentUser) return 'Unknown';
  return `${currentUser.given_name ?? ''} ${currentUser.family_name ?? ''}`.trim() || 'Unknown';
}

function formatUploadedAt(ms: number | undefined): string {
  if (ms == null) return '';
  return new Date(ms).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export default function AdminDocFormContainer() {
  const navigate = useNavigate();
  const { adminDocId } = useParams();
  const { currentUser } = useAppContext();
  const [adminDoc, setAdminDoc] = useState<AdminDoc>(initialValues);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [replacingIdx, setReplacingIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceFileInputRef = useRef<HTMLInputElement>(null);
  const replaceTargetIdxRef = useRef<number | null>(null);

  useEffect(() => {
    async function onLoad() {
      if (!adminDocId || adminDocId === 'new') {
        setIsLoading(false);
        return;
      }
      try {
        const response = await getAdminDoc(adminDocId);
        setAdminDoc(response);
      } catch (e) {
        onError(e);
      }
      setIsLoading(false);
    }
    onLoad();
  }, [adminDocId]);

  const normalizeDocuments = (docs: AdminDocFile[]) =>
    docs.map((d) => ({
      fileName: d.fileName,
      fileUrl: d.fileUrl,
      label: d.label ?? '',
      uploadedBy: d.uploadedBy ?? 'Unknown',
      uploadedBySub: d.uploadedBySub ?? '',
      uploadedAt: d.uploadedAt ?? 0,
      publicUrl: d.publicUrl,
      ...(d.isPublic ? { isPublic: true } : {}),
    }));

  const onSubmit = async (values: AdminDoc) => {
    setIsSubmitting(true);
    const documents = normalizeDocuments(values.documents);
    try {
      if (adminDocId === 'new') {
        await createAdminDoc({
          name: values.name,
          division: values.division,
          department: values.department,
          status: values.status,
          description: values.description,
          documents,
        });
      } else {
        await updateAdminDoc({
          ...adminDoc,
          name: values.name,
          division: values.division,
          department: values.department,
          status: values.status,
          description: values.description,
          documents,
        });
      }
      navigate('/admin-docs');
    } catch (e: any) {
      onError(e);
      setIsSubmitting(false);
    }
  };

  async function handleFileSelect(
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: AdminDocFile[]) => void,
    currentDocuments: AdminDocFile[],
    division: AdminDocDivision
  ) {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    e.target.value = '';
    const uploadedBy = getUploaderDisplayName(currentUser);
    const uploadedBySub = currentUser?.sub ?? '';
    const uploadedAt = Date.now();
    try {
      setIsUploading(true);
      const uploadOne = async (file: File): Promise<AdminDocFile> => {
        const { uploadUrl, fileKey, publicUrl } = await getAdminDocUploadUrl(
          file.name,
          adminDocId === 'new' ? undefined : adminDocId,
          divisionToBrand(division)
        );
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!putRes.ok) throw new Error(`Upload failed for ${file.name}`);
        return {
          fileName: file.name,
          fileUrl: fileKey,
          publicUrl,
          uploadedBy,
          uploadedBySub,
          uploadedAt,
        };
      };
      const newDocs = await Promise.all(files.map(uploadOne));
      setFieldValue('documents', [...currentDocuments, ...newDocs]);
    } catch (err: any) {
      onError(err);
    } finally {
      setIsUploading(false);
    }
  }

  function handleViewFile(doc: AdminDocFile) {
    const url = doc.publicUrl ?? doc.fileUrl;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function handlePublicChange(
    checked: boolean,
    idx: number,
    documents: AdminDocFile[],
    setFieldValue: (field: string, value: AdminDocFile[]) => void
  ) {
    const next = documents.map((d, i) =>
      i === idx ? { ...d, isPublic: checked || undefined } : d
    );
    setFieldValue('documents', next);
  }

  async function handleReplaceSelect(
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: AdminDocFile[]) => void,
    currentDocuments: AdminDocFile[],
    division: AdminDocDivision
  ) {
    const idx = replaceTargetIdxRef.current;
    if (idx === null) return;
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      setReplacingIdx(idx);
      const { uploadUrl, fileKey, publicUrl } = await getAdminDocUploadUrl(
        file.name,
        adminDocId === 'new' ? undefined : adminDocId,
        divisionToBrand(division)
      );
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      const next = currentDocuments.map((d, i) =>
        i === idx
          ? {
              ...d,
              fileName: file.name,
              fileUrl: fileKey,
              publicUrl,
              uploadedBy: getUploaderDisplayName(currentUser),
              uploadedBySub: currentUser?.sub ?? '',
              uploadedAt: Date.now(),
            }
          : d
      );
      setFieldValue('documents', next);
    } catch (err: any) {
      onError(err);
    } finally {
      setReplacingIdx(null);
      replaceTargetIdxRef.current = null;
    }
  }

  function handleRemoveDocument(
    documents: AdminDocFile[],
    idx: number,
    setFieldValue: (field: string, value: AdminDocFile[]) => void
  ) {
    setFieldValue(
      'documents',
      documents.filter((_, i) => i !== idx)
    );
  }

  function renderForm() {
    return (
      <Container>
        <Formik
          initialValues={adminDoc}
          validationSchema={AdminDocSchema}
          onSubmit={onSubmit}
          enableReinitialize={true}
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, submitCount }) => {
            const showError = (
              field: 'name' | 'division' | 'department' | 'status' | 'description'
            ) => Boolean(errors[field]) && (Boolean(touched[field]) || submitCount > 0);
            return (
            <Form onSubmit={handleSubmit}>
              <Box sx={{ mb: 3 }}>
                <h1>{adminDocId === 'new' ? 'Add New Admin Doc' : 'Edit Admin Doc'}</h1>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    id="name"
                    name="name"
                    label="Name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={showError('name')}
                    helperText={showError('name') ? errors.name : undefined}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth error={showError('division')}>
                    <InputLabel>Division</InputLabel>
                    <Select
                      id="division"
                      name="division"
                      value={values.division || ''}
                      label="Division"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <MenuItem value={AdminDocDivision.CCL}>CCL</MenuItem>
                      <MenuItem value={AdminDocDivision.TSF}>TSF</MenuItem>
                      <MenuItem value={AdminDocDivision.Company}>Company</MenuItem>
                      <MenuItem value={AdminDocDivision.OnboardingCCL}>CCL Onboarding</MenuItem>
                      <MenuItem value={AdminDocDivision.OnboardingTSF}>TSF Onboarding</MenuItem>
                    </Select>
                    {showError('division') && (
                      <FormHelperText>{errors.division}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth error={showError('department')}>
                    <InputLabel>Department</InputLabel>
                    <Select
                      id="department"
                      name="department"
                      value={values.department ?? ''}
                      label="Department"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      {ADMIN_DOC_DEPARTMENTS.map((dept) => (
                        <MenuItem key={dept} value={dept}>
                          {dept}
                        </MenuItem>
                      ))}
                    </Select>
                    {showError('department') && (
                      <FormHelperText>{errors.department}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth error={showError('status')}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      id="status"
                      name="status"
                      value={values.status ?? AdminDocStatus.Open}
                      label="Status"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <MenuItem value={AdminDocStatus.Open}>Open</MenuItem>
                      <MenuItem value={AdminDocStatus.Done}>Done</MenuItem>
                    </Select>
                    {showError('status') && (
                      <FormHelperText>{errors.status}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={7}
                    id="description"
                    name="description"
                    label="Description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={showError('description')}
                    helperText={showError('description') ? errors.description : undefined}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Documents
                  </Typography>
                  {values.documents.length > 0 && (
                    <List
                      dense
                      disablePadding
                      sx={{
                        mb: 2,
                        '& .MuiListItem-root': {
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      {values.documents.map((doc, idx) => {
                        return (
                        <ListItem
                          key={idx}
                          alignItems="flex-start"
                          sx={{ gap: 1.5 }}
                        >
                          <InsertDriveFileOutlinedIcon
                            sx={{ color: 'text.secondary', fontSize: 22, mt: 1.25, flexShrink: 0 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <TextField
                              size="small"
                              placeholder="Display name"
                              value={doc.label ?? ''}
                              onChange={(e) => {
                                const updated = values.documents.map((d, i) =>
                                  i === idx ? { ...d, label: e.target.value } : d
                                );
                                setFieldValue('documents', updated);
                              }}
                              fullWidth
                              sx={{ mb: 0.5 }}
                              slotProps={{ input: { style: { fontSize: 13 } } }}
                            />
                            <Link
                              component="button"
                              type="button"
                              variant="caption"
                              underline="hover"
                              onClick={() => handleViewFile(doc)}
                              sx={{ textAlign: 'left', display: 'block' }}
                            >
                              {doc.fileName}
                            </Link>
                            {(doc.uploadedBy || doc.uploadedAt) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {`Uploaded by ${doc.uploadedBy ?? 'Unknown'}${doc.uploadedAt ? ` on ${formatUploadedAt(doc.uploadedAt)}` : ''}`}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, mt: 0.5, width: 260, justifyContent: 'flex-end' }}>
                            <FormControlLabel
                              label="Public"
                              sx={{ mr: 0 }}
                              control={
                                <Switch
                                  size="small"
                                  checked={Boolean(doc.isPublic)}
                                  disabled={isSubmitting}
                                  onChange={(_, checked) =>
                                    handlePublicChange(
                                      checked,
                                      idx,
                                      values.documents,
                                      setFieldValue
                                    )
                                  }
                                />
                              }
                            />
                            <Tooltip title="View file">
                              <IconButton
                                size="small"
                                aria-label="View file"
                                onClick={() => handleViewFile(doc)}
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {doc.publicUrl && (
                              <Tooltip title="Copy URL">
                                <IconButton
                                  size="small"
                                  aria-label="Copy URL"
                                  onClick={() => navigator.clipboard.writeText(doc.publicUrl!)}
                                >
                                  <ContentCopyOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Replace file">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Replace file"
                                  disabled={replacingIdx === idx}
                                  onClick={() => {
                                    replaceTargetIdxRef.current = idx;
                                    replaceFileInputRef.current?.click();
                                  }}
                                >
                                  {replacingIdx === idx
                                    ? <CircularProgress size={14} />
                                    : <UploadFileOutlinedIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Remove file">
                              <IconButton
                                size="small"
                                aria-label="Remove file"
                                onClick={() =>
                                  handleRemoveDocument(
                                    values.documents,
                                    idx,
                                    setFieldValue
                                  )
                                }
                                color="error"
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </ListItem>
                        );
                      })}
                    </List>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) =>
                      handleFileSelect(e, setFieldValue, values.documents, values.division)
                    }
                  />
                  <input
                    ref={replaceFileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) =>
                      handleReplaceSelect(e, setFieldValue, values.documents, values.division)
                    }
                  />
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading || isSubmitting}
                  >
                    {isUploading ? 'Uploading...' : 'Add files'}
                  </Button>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                      onClick={() => handleSubmit()}
                    >
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => navigate('/admin-docs')}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
            );
          }}
        </Formik>
      </Container>
    );
  }

  return (
    <main id="main">
      {isLoading ? <CircularProgressComponent /> : renderForm()}
    </main>
  );
}
